// SPDX-License-Identifier: AGPL-3.0-or-later

import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import {shouldShowPremiumFeatures} from '@app/features/premium/utils/PremiumUtils';
import {Button} from '@app/features/ui/button/Button';
import styles from '@app/features/ui/plutonium_upsell/PlutoniumUpsell.module.css';
import {Trans, useLingui} from '@lingui/react/macro';
import {CrownIcon, InfoIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type React from 'react';

interface PlutoniumUpsellProps {
	children: React.ReactNode;
	className?: string;
	buttonText?: React.ReactNode;
	onButtonClick?: () => void;
	dismissible?: boolean;
	onDismiss?: () => void;
}
export const PlutoniumUpsell: React.FC<PlutoniumUpsellProps> = ({
	children,
	className,
	buttonText,
	onButtonClick,
	dismissible,
	onDismiss,
}) => {
	const showPremiumFeatures = shouldShowPremiumFeatures();
	const {t} = useLingui();
	if (!showPremiumFeatures) {
		return (
			<div className={clsx(styles.selfHostedNotice, className)}>
				<InfoIcon size={16} weight="fill" className={styles.selfHostedIcon} />
				<div className={styles.selfHostedTextContainer}>
					<div className={styles.selfHostedText}>{children}</div>
					<p className={styles.selfHostedText}>
						<Trans>This feature has been disabled by the instance administrator.</Trans>
					</p>
				</div>
			</div>
		);
	}
	return (
		<div className={clsx(styles.upsell, className)}>
			<CrownIcon size={16} weight="fill" className={styles.icon} />
			<div className={styles.content}>
				<div className={styles.text}>{children}</div>
				<div className={styles.actions}>
					<Button
						variant="inverted"
						superCompact={true}
						fitContent={true}
						onClick={onButtonClick ?? (() => PremiumModalCommands.open())}
						aria-label={t`Get ${PREMIUM_PRODUCT_NAME}`}
					>
						{buttonText ?? <Trans>Get {PREMIUM_PRODUCT_NAME}</Trans>}
					</Button>
					{dismissible && onDismiss && (
						<button type="button" className={styles.dismissLink} onClick={onDismiss}>
							<Trans>Don't show this again</Trans>
						</button>
					)}
				</div>
			</div>
		</div>
	);
};
